import React from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import './index.scss';

const DIGIT_SEQUENCE = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const ANIMATABLE_NUMBER_PATTERN = /^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?\s*[kmbt%]?$/i;

function namespaceClass(namespace, element) {
  return namespace ? `${namespace}__${element}` : null;
}

function getPrimitiveValueText(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return null;
}

function canAnimateValue(valueText) {
  return Boolean(valueText && /\d/.test(valueText) && ANIMATABLE_NUMBER_PATTERN.test(valueText));
}

function renderAnimatedValue(valueText, statIndex) {
  if (!canAnimateValue(valueText)) {
    return valueText;
  }

  return valueText.split('').map((char, charIndex) => {
    const delay = `${100 + statIndex * 90 + charIndex * 36}ms`;
    if (/^[0-9]$/.test(char)) {
      const digit = Number(char);
      return (
        <span
          className="editorial-hero-stats-rail__value-char editorial-hero-stats-rail__value-char--digit"
          key={`${char}-${charIndex}`}
          style={{
            '--editorial-stat-digit-y': `${digit * -1.08}em`,
            '--editorial-stat-delay': delay,
          }}
        >
          <span className="editorial-hero-stats-rail__digit-strip">
            {DIGIT_SEQUENCE.map((item) => (
              <span className="editorial-hero-stats-rail__digit" data-digit={item} key={item} />
            ))}
          </span>
        </span>
      );
    }

    return (
      <span
        className="editorial-hero-stats-rail__value-char editorial-hero-stats-rail__value-char--static"
        key={`${char}-${charIndex}`}
        style={{ '--editorial-stat-delay': delay }}
      >
        {char === ' ' ? '\u00a0' : char}
      </span>
    );
  });
}

export default function EditorialHeroStatsRail({
  stats,
  namespace,
  className = '',
  ariaLabel,
}) {
  if (!Array.isArray(stats) || stats.length === 0) return null;

  return (
    <div
      className={classNames(
        'editorial-hero-stats-rail',
        namespaceClass(namespace, 'stats'),
        className,
      )}
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
    >
      {stats.map((stat, idx) => (
        <div
          className={classNames(
            'editorial-hero-stats-rail__item',
            namespaceClass(namespace, 'stat'),
          )}
          key={stat.id || idx}
        >
          <div
            className={classNames(
              'editorial-hero-stats-rail__value',
              namespaceClass(namespace, 'stat-num'),
            )}
          >
            {(() => {
              const valueText = getPrimitiveValueText(stat.value);
              if (!valueText) {
                return stat.value;
              }

              return (
                <>
                  <span className="editorial-hero-stats-rail__value-animated" aria-hidden="true" key={valueText}>
                    {renderAnimatedValue(valueText, idx)}
                  </span>
                  <span className="editorial-hero-stats-rail__value-readable">{valueText}</span>
                </>
              );
            })()}
          </div>
          <div
            className={classNames(
              'editorial-hero-stats-rail__label',
              namespaceClass(namespace, 'stat-label'),
            )}
          >
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
}

EditorialHeroStatsRail.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.node,
    label: PropTypes.node,
  })),
  namespace: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};
